/**
 * Fleet Activity Log — recent notification events in table format.
 * Columns: Event, Property, Time, Source, Status.
 * Authority: Fleet Dashboard design (Pencil mock) §Activity Log.
 */

import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { NotificationActivityRow } from '@/hooks/useNotificationActivity';

interface FleetActivityLogProps {
  events: NotificationActivityRow[];
  onViewAll?: () => void;
}

function getStatusBadge(outcome: string): { text: string; className: string } {
  switch (outcome) {
    case 'sent':
      return {
        text: 'Sent',
        className: 'bg-[hsl(var(--color-warning))] text-wt-amber-primary',
      };
    case 'resolved':
      return {
        text: 'Resolved',
        className: 'bg-[hsl(var(--green-primary)/0.15)] text-wt-green-primary',
      };
    case 'suppressed':
      return {
        text: 'Suppressed',
        className: 'bg-muted text-muted-foreground',
      };
    default:
      return {
        text: 'Logged',
        className: 'bg-muted text-foreground',
      };
  }
}

function getSourceLabel(event: NotificationActivityRow): string {
  if (event.outcome === 'resolved') return 'Auto-resolved';
  if (event.event_kind.includes('curtailment') || event.event_kind.includes('recovery')) return 'Automation';
  if (event.event_kind.includes('forecast') || event.event_kind.includes('overnight')) return 'Forecast';
  return 'Alert';
}

function formatRelativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '—';
  }
}

export function FleetActivityLog({ events, onViewAll }: FleetActivityLogProps) {
  const rows = events.slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-mono text-base font-semibold text-foreground">Activity Log</h3>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="rounded-full border border-border px-4 py-2 text-sm font-mono font-medium text-foreground hover:bg-accent transition-colors"
          >
            View All
          </button>
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* Header */}
        <div className="flex bg-muted">
          <div className="flex-1 px-3 py-3">
            <span className="font-mono text-sm text-muted-foreground">Event</span>
          </div>
          <div className="w-[120px] shrink-0 px-3 py-3">
            <span className="font-mono text-sm text-muted-foreground">Property</span>
          </div>
          <div className="w-[120px] shrink-0 px-3 py-3">
            <span className="font-mono text-sm text-muted-foreground">Time</span>
          </div>
          <div className="w-[120px] shrink-0 px-3 py-3">
            <span className="font-mono text-sm text-muted-foreground">Source</span>
          </div>
          <div className="w-[120px] shrink-0 px-3 py-3">
            <span className="font-mono text-sm text-muted-foreground">Status</span>
          </div>
        </div>
        {/* Rows */}
        {rows.map((event) => {
          const status = getStatusBadge(event.outcome);
          return (
            <div
              key={event.id}
              className="flex border-b border-border last:border-b-0"
            >
              <div className="flex-1 px-3 py-3">
                <span className="font-mono text-xs text-foreground">{event.event_kind}</span>
              </div>
              <div className="w-[120px] shrink-0 px-3 py-3">
                <span className="text-[13px] text-foreground truncate block">
                  {event.property_name}
                </span>
              </div>
              <div className="w-[120px] shrink-0 px-3 py-3">
                <span className="text-[13px] text-foreground">
                  {formatRelativeTime(event.created_at)}
                </span>
              </div>
              <div className="w-[120px] shrink-0 px-3 py-3">
                <span className="text-[13px] text-foreground">{getSourceLabel(event)}</span>
              </div>
              <div className="w-[120px] shrink-0 px-3 py-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-1 text-xs font-mono',
                    status.className
                  )}
                >
                  {status.text}
                </span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No recent activity.
          </div>
        )}
      </div>
    </div>
  );
}
